<template>
  <main>
    <div class="split-row row">
      <BaseChart
        class="box-shadow"
        ref="pieChart"
        title="Job Distribution"
        subtitle="Total jobs by status"
        chart-type="pie"
        :data-processor="MakePieData"
        :extract-unique-items="jobs => MakePieData(jobs).map(d => d.name)"
        :color-map="stateColorMap"
      />
      <BaseChart
        class="box-shadow"
        chart-type="line"
        ref="lineChart"
        title="Job Timeline"
        subtitle="Jobs over 24 hours"
        :data-processor="jobTimelineDataProcessor"
        :color-map="stateColorMap"
      />
    </div>
    <div class="equal-row row">
      <BaseChart
        class="box-shadow"
        chart-type="bar"
        ref="processDetails"
        title="Process Performance"
        subtitle="Status rates by process"
        :data-processor="processPerformanceDataProcessor"
        :color-map="stateColorMap"
        :category-extractor="item => item.releaseName"
        :label-y-axis="'Number of jobs'"
      />
      <BaseChart
        class="box-shadow"
        ref="averageCompletion"
        title="Runtime by Day"
        subtitle="Average job execution time across each day"
        chart-type="line"
        :show-legend="false"
        :data-processor="averageCompletionDataProcessor"
      />
    </div>
    <div class="split-row-right row">
      <BaseChart
        class="box-shadow"
        chart-type="bar"
        ref="tenantInteraction"
        title="Processes by Tenant"
        subtitle="Breakdown of background vs foreground processes"
        :data-processor="tenantInteractionProcessor"
        :category-extractor="item => item.tenant"
        :label-y-axis="'Processes'"
      />

      <BaseChart
        class="box-shadow"
        ref="hostUtilization"
        title="Host Utilization"
        subtitle="Number of jobs run by host"
        chart-type="pie"
        :data-processor="MakeHostPieData"
        :extract-unique-items="extractUniqueHosts"
      />
    </div>
  </main>
</template>

<script setup>
import { getCurrentInstance, onMounted } from 'vue'

import { getJobs } from '@/Services/Ressources/API/JobService'
import { getProcessesMapped } from '@/Services/Ressources/API/ProcessService'

import { averageCompletionDataProcessor } from '@/Modules/Grafer/dataProcessor/averageCompletionComposable'
import { extractUniqueHosts, MakeHostPieData, MakePieData } from '@/Modules/Grafer/dataProcessor/PieData'
import { jobTimelineDataProcessor } from '@/Modules/Grafer/dataProcessor/LineData'
import { processPerformanceDataProcessor } from '@/Modules/Grafer/dataProcessor/processPerformanceDataProcessor'
import { tenantInteractionProcessor } from '@/Modules/Grafer/tenantInteractionDataProcessor'

import { stateColorMap } from '@/Modules/Grafer/common/stateMappings'

import BaseChart from '@/components/Graph/BaseChart.vue'

const { proxy } = getCurrentInstance();

const fetchJobs = async () => {
  let jobDataResultDaily = null;
  let jobDataResultWeekly = null;
  let processesResult = null;

  try {
    [jobDataResultDaily, jobDataResultWeekly, processesResult] = await Promise.all([
      getJobs(),
      getJobs("7days"),
      getProcessesMapped()
    ]);
  } catch (err) {
    console.error("Error fetching one or more datasets:", err);
  }

  const chartDataMap = {
    averageCompletion: jobDataResultWeekly,
    lineChart: jobDataResultDaily,
    pieChart: jobDataResultDaily,
    processDetails: jobDataResultDaily,
    hostUtilization: jobDataResultDaily,
    tenantInteraction: processesResult,
  };

  for (const [key, data] of Object.entries(chartDataMap)) {
    try {
      if (!data) throw new Error(`Missing data for ${key}`);
      proxy.$refs[key]?.populateComponent(data);
    } catch (err) {
      proxy.$refs[key]?.populateComponent(null); 
    }
  }
};
onMounted(fetchJobs);
</script>

<style scoped>
.box-shadow {
  border: var(--border-card);
  border-radius: var(--border-radius-card);
  box-shadow: var(--shadow-card);
  margin: 8px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.box-shadow:hover {
  box-shadow: var(--shadow-card-hover);
  transform: translateY(-2px);
}

main {
  display: grid;
  gap: 16px;
  padding: 16px;
}

.split-row-right {
  grid-template-columns: 2.5fr 1fr;
}

.split-row {
  grid-template-columns: 1fr 2.5fr;
}

.equal-row {
  grid-template-columns: 1fr 1fr;
}

.row {
  display: grid;
  gap: 5px;
  align-items: start;
}

main section h2 {
  padding: 1rem 0.5rem;
}
</style>